import { Alert, Group, Switch, Text, Tooltip } from "@mantine/core";
import { TIssueStatus } from "@mctypes/global.types";
import { IIssueBoardApi } from "@mcapps/shared";
import { ColDef } from "ag-grid-community";
import React from "react";
import { FailedAlert, MCLoader } from "../../../../components";
import ViewEditTable from "../../../../components/ViewEditTable/ViewEditTable";
import { VETEmptyValidator } from "../../../../components/ViewEditTable/ViewEditTableValidators";
import { useAppSelector } from "../../../../hooks";
import {
  usePutProjectByProjectIdIssueboardAndBoardIdBucketsMutation,
  usePutProjectByProjectIdIssueboardAndBoardIdUsebucketsasworkflowMutation,
} from "../../../../../openapi";
import { ISSUE_STATES_OPTIONS } from "../issueConstants";
import SettingsCardWrapper from "./SettingsCardWrapper";

type IVETBoard = { value: string; status: TIssueStatus; order: number };

const IssueBoardBuckets: React.FC<{ issueBoard: IIssueBoardApi }> = ({ issueBoard }) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const { buckets, useBucketsAsWorkflow } = issueBoard;

  const [updateBuckets, { isLoading: updateBucketsLoading, error: errorBuckets }] =
    usePutProjectByProjectIdIssueboardAndBoardIdBucketsMutation();
  const [updateUseAsWorkflow, { isLoading: updateUseAsWorkflowLoading, error: errorWorkflow }] =
    usePutProjectByProjectIdIssueboardAndBoardIdUsebucketsasworkflowMutation();

  const columns = React.useMemo<ColDef<IVETBoard>[]>(() => {
    const _columns: ColDef<IVETBoard>[] = [
      {
        field: "value",
        headerName: "Navn",
        editable: true,
      },
    ];

    if (useBucketsAsWorkflow === true) {
      _columns.push({
        field: "status",
        headerName: "Status",
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ISSUE_STATES_OPTIONS.map((state) => state.value),
        },
      });
    }
    return _columns;
  }, [useBucketsAsWorkflow]);

  const tableData = React.useMemo<IVETBoard[]>(() => {
    return buckets?.map((bucket, index) => {
      return {
        order: bucket.order,
        value: bucket.value,
        status: bucket.status || "Opprettet",
      };
    });
  }, [buckets]);

  const newItemTemplateObject: IVETBoard = {
    value: "",
    status: "Opprettet",
    order: 0,
  };

  const saveBuckets = async (buckets: IVETBoard[]) => {
    if (buckets.length === 0) {
      alert("Du må ha minst 1 bucket");
      return;
    }

    updateBuckets({
      projectId: project._id,
      boardId: issueBoard._id,
      body: {
        buckets,
      },
    });
  };

  const isLoading = updateBucketsLoading || updateUseAsWorkflowLoading;
  const error = errorBuckets != null || errorWorkflow != null;

  return (
    <SettingsCardWrapper title={issueBoard?.bucketNameSetting}>
      {isLoading === true && <MCLoader loading={isLoading} />}
      {error === true && <FailedAlert />}
      <Group gap={"4px"}>
        <Tooltip
          withArrow
          multiline
          w={300}
          label={`Når ${issueBoard?.bucketNameSetting?.toLowerCase()} endres, endres den tilsvarende statusen. Brukes hovedsakelig i kanban hvor du kan bygge en arbeidsflyt som beveger seg fra venstre til høyre`}
        >
          <i className="fa fa-info-circle fa-fw icon-info" />
        </Tooltip>
        <Text size="sm">Bruk {issueBoard?.bucketNameSetting?.toLowerCase()} som arbeidsflyt:</Text>
        <Switch
          size="sm"
          onChange={(event) =>
            updateUseAsWorkflow({
              projectId: project._id,
              boardId: issueBoard._id,
              body: {
                useBucketsAsWorkflow: event.currentTarget.checked,
              },
            })
          }
          checked={useBucketsAsWorkflow || false}
        />
      </Group>
      <Alert color="yellow" variant="light" py="xs" w={"fit-content"}>
        <Text fw={500}>Endringer på eksisterende buckets vil ikke oppdatere buckets på aktuelle saker. </Text>
      </Alert>
      <ViewEditTable
        validators={[VETEmptyValidator]}
        canEdit={true}
        orderProp={"order"}
        columns={columns}
        data={tableData}
        onSave={saveBuckets}
        newItemTemplateObject={newItemTemplateObject}
      />
    </SettingsCardWrapper>
  );
};

export default IssueBoardBuckets;
